'use client';

import Button from '@/components/Button';
import Image from '@/components/Image';
import Tooltip from '@/components/Tooltip';
import ZeeloolCheckbox from '@/components/ZeeloolCheckBox';
import { useStore } from '@/providers/app-store-provider';
import { colors, font } from '@/theme/zeelool.theme';
import { CUSTOM_VALUE } from '@/utils/constants';
import { Collapse, Slider } from '@mui/material';
import { CollapseArrowDownOutlined, MinusOutlined, PlusOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface FilterOption {
  id: string;
  title: string;
  img?: string;
}

interface FilterSection {
  id: string;
  title: string;
  type: 'checkbox' | 'radio' | 'slider';
  options?: FilterOption[];
  min?: number;
  max?: number;
  step?: number;
}

interface GoodsFilterProps {
  values: Record<string, any>; // 当前筛选值
  onValueChange: (filterId: string, value: any) => void; // 单个筛选项变化
  onClear: () => void; // 清空筛选
  onApply?: (values: Record<string, any>) => void; // 应用筛选
  isWeb?: boolean;
  expandedSections?: Record<string, boolean>; // 外部传入的展开状态
  onToggleSection?: (sectionId: string) => void; // 外部传入的切换函数
  isPopup?: boolean;
  sdProductListPageFilterSubmitBtnClick?: (filter: any) => void;
}

export default function GoodsFilter({
  values = {},
  onValueChange,
  onClear,
  onApply,
  isWeb = false,
  expandedSections: externalExpandedSections,
  onToggleSection: externalOnToggleSection,
  isPopup,
  sdProductListPageFilterSubmitBtnClick,
}: GoodsFilterProps) {
  const waiMode = useStore((state) => state.waiMode);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const t = useTranslations('goodsList.goodsFilter');
  const globalT = useTranslations('cart');
  const goodsFilterRef = useRef<HTMLDivElement>(null);
  const customSizeRef = useRef<HTMLDivElement>(null);
  const product_filter_new = useMemo(() => {
    try {
      return publicResourceData.config_names.product_filter_new;
    } catch {
      return [];
    }
  }, [publicResourceData]);

  const [templeValue, setTempleValue] = useState<number[]>([0, 150]);
  const [frameWidthValue, setFrameWidthValue] = useState<number[]>([0, 150]);
  // 响应values变化 更新slideValue
  useEffect(() => {
    setTempleValue(values.temple || [0, 150]);
    setFrameWidthValue(values.frame_width || [0, 150]);
  }, [values]);

  const goodsFilters = useMemo(() => {
    return product_filter_new.map((item) => {
      let type: 'checkbox' | 'radio' | 'slider' = 'checkbox';
      if (item.attribute_code === 'sell_price' || item.attribute_code === 'product_size') {
        type = 'radio';
      }
      return {
        id: item.attribute_code,
        title: item.attribute_name?.toLowerCase(),
        type,
        options: item.attribute_values.map((vItem) => {
          return {
            id: vItem.value || vItem.name?.toLowerCase(),
            title: vItem.name,
            img: vItem.image_url,
          };
        }),
      };
    });
  }, [product_filter_new]);

  // 管理每个筛选项的展开/收起状态（内部状态，用于没有外部状态时）
  const [internalExpandedSections, setInternalExpandedSections] = useState<Record<string, boolean>>(
    () => {
      // 默认所有筛选项都展开
      const initialState: Record<string, boolean> = {};
      // 这里不能直接使用goodsFilters，因为它可能还没初始化
      return initialState;
    },
  );

  // 初始化展开状态
  useEffect(() => {
    if (goodsFilters.length > 0) {
      setInternalExpandedSections((prev) => {
        const newState = { ...prev };
        goodsFilters.forEach((section) => {
          // 只有当状态尚未设置时才设置为true，避免覆盖用户的选择
          if (!(section.id in newState)) {
            newState[section.id] = true;
          }
        });
        return newState;
      });
    }
  }, [goodsFilters]);

  // 使用外部状态或内部状态
  const expandedSections = externalExpandedSections || internalExpandedSections;

  // 切换展开/收起状态
  const toggleSection = useCallback(
    (sectionId: string) => {
      if (externalOnToggleSection) {
        // 使用外部的切换函数
        externalOnToggleSection(sectionId);
      } else {
        // 使用内部的切换函数
        setInternalExpandedSections((prev) => ({
          ...prev,
          [sectionId]: !prev[sectionId],
        }));
      }
    },
    [externalOnToggleSection],
  );

  // 计算筛选总数
  const getFilterTotal = useCallback(() => {
    let total = 0;
    Object.keys(values).forEach((key: string) => {
      const value = values[key];
      if (['temple', 'frame_width'].includes(key)) {
        return;
      }
      // 排除temple和frame_width
      if (Array.isArray(value)) {
        total += value.length;
      } else if (value && value !== '') {
        total += 1;
      }
    });
    return total;
  }, [values]);

  // 处理复选框变化
  const handleCheckboxChange = useCallback(
    (filterId: string, optionId: string, checked: boolean) => {
      const currentValue = values[filterId] || [];
      let newValue;

      if (checked) {
        newValue = Array.isArray(currentValue) ? [...currentValue, optionId] : [optionId];
      } else {
        newValue = Array.isArray(currentValue)
          ? currentValue.filter((id: string) => id !== optionId)
          : [];
      }
      // 统一调用onValueChange，具体行为由父组件决定
      onValueChange(filterId, newValue);
    },
    [values, onValueChange],
  );

  // 处理单选框变化
  const handleRadioChange = useCallback(
    (filterId: string, value: string) => {
      // 如果点击的是当前选中的值，则取消选择
      const currentValue = values[filterId];
      const newValue = currentValue === value ? '' : value;
      if (filterId === 'product_size' && newValue === CUSTOM_VALUE) {
        // 滚动goodsFilter到底部
        scrollToCustomSize();
      }
      // 统一调用onValueChange，具体行为由父组件决定
      onValueChange(filterId, newValue);
    },
    [values, onValueChange],
  );
  const scrollToCustomSize = () => {
    const pcBottomAnchor = document.querySelector('.pc-filter-bottom-anchor');
    if (pcBottomAnchor) {
      pcBottomAnchor.scrollIntoView({ behavior: 'smooth' });
    }
  };
  // 处理勾选custom以后，把slider滚动至可见
  useEffect(() => {
    if (
      sessionStorage.getItem('zl_customSizeRef') &&
      customSizeRef.current &&
      goodsFilterRef.current
    ) {
      // 获取自定义尺寸元素相对于筛选器容器的位置
      const containerRect = goodsFilterRef.current.getBoundingClientRect();
      const targetRect = customSizeRef.current.getBoundingClientRect();
      const relativeTop = targetRect.top - containerRect.top + goodsFilterRef.current.scrollTop;

      // 只滚动筛选器容器，不影响页面其他部分
      goodsFilterRef.current.scrollTo({
        top: relativeTop - 20, // 减去20px作为padding
        behavior: 'smooth',
      });

      sessionStorage.removeItem('zl_customSizeRef');
    }
  }, [customSizeRef.current]);

  // 处理滑块变化
  const handleSliderChangeCommit = useCallback(
    (sliderKey: string, value: number | number[]) => {
      // 统一调用onValueChange，具体行为由父组件决定
      onValueChange(sliderKey, value);
    },
    [onValueChange],
  );
  // 处理滑块变化 实时更新slideValue
  const handleSliderChange = (sliderKey: string, value: number | number[]) => {
    if (sliderKey === 'temple') {
      setTempleValue(value as number[]);
    } else if (sliderKey === 'frame_width') {
      setFrameWidthValue(value as number[]);
    }
  };

  // 处理清空 - 直接调用父组件的onClear
  const handleClear = useCallback(() => {
    onClear();
  }, [onClear]);

  // 处理应用 - 直接调用父组件的onApply
  const handleApply = () => {
    onApply?.(values);
    sdProductListPageFilterSubmitBtnClick?.(values);
  };

  // 跟踪是否是首次渲染
  const isFirstRender = useRef(true);
  useEffect(() => {
    // 跳过首次渲染
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (isPopup) return;
    sdProductListPageFilterSubmitBtnClick?.(values);
  }, [values, isPopup]);

  // 计算grid布局的className
  const getGridClassName = useCallback(
    ({
      isFrameColor,
      isPrescription,
      isFrameShape,
    }: {
      isFrameColor: boolean;
      isPrescription: boolean;
      isFrameShape: boolean;
    }) => {
      const baseClasses = ['grid'];

      if (isWeb) {
        // Web端样式
        if (isPopup) {
          if (isFrameShape) {
            baseClasses.push('gap-x-[60px] gap-y-[16px]');
            baseClasses.push('grid-cols-2');
          } else if (isFrameColor) {
            baseClasses.push('gap-[24px]');
            baseClasses.push('grid-cols-9');
          } else if (isPrescription) {
            baseClasses.push('gap-[16px]');
            baseClasses.push('grid-cols-1');
          } else {
            baseClasses.push('gap-x-[30px] gap-y-[16px]');
            baseClasses.push('grid-cols-3');
          }
        } else {
          baseClasses.push('gap-[16px]');
          baseClasses.push(isFrameColor ? 'grid-cols-5 !gap-24' : 'grid-cols-1');
        }
      } else {
        // 移动端样式
        baseClasses.push('gap-[10px]');
        baseClasses.push(isFrameColor || isPrescription ? 'grid-cols-2' : 'grid-cols-3');
      }

      return baseClasses.join(' ');
    },
    [isWeb],
  );

  const sliderStyle = useMemo(() => {
    let width = '286px';
    let trackHeight = 5;
    let height = 5;
    let thumbHeight = 16;
    if (isWeb) {
      width = '208px';
      height = 3;
      trackHeight = 3;
      thumbHeight = 14;
      if (isPopup) {
        width = '360px';
        height = 5;
        trackHeight = 5;
        thumbHeight = 16;
      }
    }
    return {
      color: colors.primaryPurple,
      height,
      padding: '15px 0',
      width,
      left: '50%',
      transform: 'translateX(-50%)',
      '& .MuiSlider-thumb': {
        height: thumbHeight,
        width: thumbHeight,
        borderRadius: '50%',
        border: '4px solid #fff',
        borderColor: colors.primaryPurple,
        backgroundColor: '#fff',
        boxShadow: '0 0 2px 0px rgba(0, 0, 0, 0.1)',
      },
      '& .MuiSlider-valueLabel': {
        fontSize: 12,
        fontWeight: 'normal',
        top: -10,
      },
      '& .MuiSlider-track': {
        border: 'none',
        height: trackHeight,
      },
    };
  }, [isWeb, isPopup]);

  // 计算checkbox选项的className
  const getCheckboxOptionClassName = useCallback(
    (
      checked: boolean,
      isFrameShape: boolean,
      isFrameColor: boolean,
      isPrescription: boolean,
      titleLength = 0,
    ) => {
      if (isWeb) {
        const baseClasses = [
          'flex',
          'items-center',
          'text-14',
          'cursor-pointer',
          'h-[20px]',
          'group',
          'w-max',
        ];
        return baseClasses.join(' ');
      }
      const baseClasses = [
        'flex',
        'items-center',
        'text-12',
        'rounded-[6px]',
        'cursor-pointer',
        'h-[40px]',
      ];

      // 选中状态样式
      if (checked) {
        baseClasses.push('bg-[#f6f6fe]', 'text-primary-purple', 'font-bb');
      } else {
        baseClasses.push('bg-[#f4f4f4]');
      }

      // 特殊类型的样式
      if (isFrameShape) {
        baseClasses.push('h-[92px]');
      }

      if (isFrameColor) {
        baseClasses.push('justify-start', 'pl-12');
      } else {
        baseClasses.push('justify-center');
      }

      // 长标题跨列显示（仅移动端）
      if (isPrescription && titleLength > 20) {
        baseClasses.push('col-span-2');
      }

      return baseClasses.join(' ');
    },
    [isWeb],
  );

  // 计算内部容器的className
  const getOptionContentClassName = useCallback(
    (isFrameShape: boolean) => {
      const baseClasses = ['flex', 'items-center'];
      if (isWeb) {
        baseClasses.push('flex-row');
      } else {
        baseClasses.push(isFrameShape ? 'flex-col' : 'flex-row');
      }
      return baseClasses.join(' ');
    },
    [isWeb],
  );

  // 计算radio选项的className
  const getRadioOptionClassName = useCallback(
    (selected: boolean) => {
      if (isWeb) {
        const baseClasses = [
          'flex',
          'items-center',
          'text-14',
          'cursor-pointer',
          'h-[20px]',
          'group',
          'w-max',
        ];
        return baseClasses.join(' ');
      }
      const baseClasses = [
        'flex',
        'items-center',
        'justify-center',
        'text-12',
        'rounded-[6px]',
        'cursor-pointer',
        'h-[40px]',
      ];

      if (selected) {
        baseClasses.push('bg-[#f6f6fe]', 'text-primary-purple', 'font-bb');
      } else {
        baseClasses.push('bg-[#f4f4f4]');
      }

      return baseClasses.join(' ');
    },
    [isWeb],
  );

  // 计算radio选项grid的className
  const getRadioGridClassName = useCallback(
    ({ isPrice, isSize }: { isPrice: boolean; isSize: boolean }) => {
      const baseClasses = ['grid'];
      if (isWeb) {
        if (isPopup) {
          if (isPrice) {
            baseClasses.push('gap-x-[30px] gap-y-[16px]', 'grid-cols-3');
          } else if (isSize) {
            baseClasses.push('gap-[60px] gap-y-[16px]', 'grid-cols-2');
          } else {
            baseClasses.push('gap-[16px]', 'grid-cols-1');
          }
        } else {
          baseClasses.push('gap-[16px]', 'grid-cols-1');
        }
      } else {
        baseClasses.push('gap-[10px]', 'grid-cols-3');
      }

      return baseClasses.join(' ');
    },
    [isWeb, isPopup],
  );

  // 计算图片容器的className
  const getImageContainerClassName = useCallback(
    (isFrameShape: boolean, isFrameColor: boolean) => {
      if (isWeb) {
        if (isFrameColor) {
          return 'w-[20px] h-[auto] flex items-center justify-center';
        }
        return 'w-[42px] h-[auto] flex items-center justify-center mr-20';
      }
      return isFrameShape ? 'w-[68px] h-[52px] flex items-center justify-center' : '';
    },
    [isWeb],
  );

  // 计算图片的className
  const getImageClassName = useCallback((isFrameShape: boolean) => {
    return isFrameShape ? 'w-[68px] h-auto block' : 'w-[20px] h-[20px] mr-8 rounded-full';
  }, []);

  // 计算筛选项容器的className
  const getFilterItemClassName = useCallback(() => {
    const baseClasses = ['border-b', 'border-gray-200'];
    if (isWeb) {
      baseClasses.push('mt-24', 'first:mt-0');
    }
    return baseClasses.join(' ');
  }, [isWeb]);

  // 计算筛选项标题的className
  const getFilterTitleClassName = useCallback(() => {
    const baseClasses = [
      'flex',
      'items-center',
      'justify-between',
      'w-full',
      'cursor-pointer',
      'transition-colors',
    ];
    if (isWeb) {
      baseClasses.push('pt-10', 'pb-20', 'h-[50px]');
    } else {
      baseClasses.push('px-4', 'h-[48px]');
    }
    return baseClasses.join(' ');
  }, [isWeb]);

  // 计算筛选项标题文本的className
  const getFilterTitleTextClassName = useCallback(() => {
    const baseClasses = ['font-bb', 'capitalize'];
    baseClasses.push(isWeb ? 'text-16' : 'text-14');
    return baseClasses.join(' ');
  }, [isWeb]);

  // 计算箭头图标的className
  const getArrowIconClassName = useCallback((isExpanded: boolean) => {
    const baseClasses = ['text-[17px]', 'text-[#999999]', 'transition-transform', 'duration-200'];
    if (isExpanded) {
      baseClasses.push('rotate-180');
    }
    return baseClasses.join(' ');
  }, []);

  const renderFilterSection = useCallback(
    (section: FilterSection) => {
      const currentValue = values[section.id];
      // ! 现在没有custom 这个选项，老版本有
      const isCustomSelected = section.id === 'product_size' && currentValue === CUSTOM_VALUE;
      const isFrameShape = section.id === 'product_frame_shape';
      const isFrameColor = section.id === 'product_frame_color';
      const isPrescription = section.id === 'prescription_type';
      const isPrice = section.id === 'sell_price';
      const isSize = section.id === 'product_size';
      switch (section.type) {
        case 'checkbox':
          return (
            <div className="w-full">
              <div className={getGridClassName({ isFrameColor, isPrescription, isFrameShape })}>
                {section.options?.map((option) => {
                  const checked = Array.isArray(currentValue) && currentValue.includes(option.id);
                  return (
                    <div
                      key={option.id}
                      className={getCheckboxOptionClassName(
                        checked,
                        isFrameShape,
                        isFrameColor,
                        isPrescription,
                        option.title?.length || 0,
                      )}
                      onClick={() => handleCheckboxChange(section.id, option.id, !checked)}
                    >
                      <div className={getOptionContentClassName(isFrameShape)}>
                        {!isFrameColor && isWeb && (
                          <ZeeloolCheckbox
                            checked={checked}
                            sx={{
                              paddingInline: '0',
                              '&:hover .PrivateSwitchBase-input ~ span': {
                                borderColor: colors.primaryPurpleHover,
                              },
                            }}
                            className="!mr-12"
                            fontSize={16}
                          />
                        )}
                        {option.img && !waiMode && (
                          <div className={getImageContainerClassName(isFrameShape, isFrameColor)}>
                            {isWeb && (
                              <>
                                {isFrameShape && (
                                  <Image
                                    src={option.img}
                                    alt={option.title}
                                    className="w-[42px] h-auto object-contain"
                                    width={42}
                                    height={42}
                                  />
                                )}
                                {isFrameColor && (
                                  <Tooltip title={option.title} placement="top" variant="light">
                                    <Image
                                      src={option.img}
                                      alt={option.title}
                                      className={`
                                    w-[20px] h-auto object-contain rounded-full
                                    ${option.title === t('white') && !checked ? 'border-1 border-[#dddddd]' : ''}
                                    ${checked ? 'border-1 border-primary-purple p-[1px]' : ''}
                                  `}
                                      width={20}
                                      height={20}
                                    />
                                  </Tooltip>
                                )}
                              </>
                            )}
                            {!isWeb && (
                              <Image
                                src={option.img}
                                alt={option.title}
                                className={getImageClassName(isFrameShape)}
                                width={isFrameShape ? 68 : 20}
                                height={isFrameShape ? 52 : 20}
                                style={isFrameShape ? { width: '68px', height: 'auto' } : undefined}
                              />
                            )}
                          </div>
                        )}
                        {isFrameColor && isWeb ? (
                          ''
                        ) : (
                          <span className={`${isWeb ? 'hover:underline' : ''}`}>
                            {option.title}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );

        case 'radio':
          return (
            <div className="w-full">
              <div className={getRadioGridClassName({ isPrice, isSize })}>
                {section.options?.map((option) => {
                  const selected = currentValue === option.id;
                  return (
                    <div
                      key={option.id}
                      className={getRadioOptionClassName(selected)}
                      onClick={() => handleRadioChange(section.id, option.id)}
                    >
                      {isWeb && (
                        <ZeeloolCheckbox
                          checked={selected}
                          sx={{
                            paddingInline: '0',
                            '&:hover .PrivateSwitchBase-input ~ span': {
                              borderColor: colors.primaryPurpleHover,
                            },
                          }}
                          className="!mr-12"
                          fontSize={16}
                        />
                      )}
                      <span className={`${isWeb ? 'hover:underline' : ''}`}>{option.title}</span>
                    </div>
                  );
                })}
              </div>

              {/* 当选择Custom时显示滑块 */}
              {isCustomSelected && (
                <div className={`${isWeb ? 'mt-[16px]' : 'mt-[13px]'}`}>
                  {/* Frame width 滑块，因为slider会在全宽的时候，数字悬浮溢出，因此Hack了一下，使用绝对定位的方式，mb这些都是为了配合绝对定位设置的 */}
                  <div className="mb-[44px]">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className={`text-12 ${isWeb ? 'font-bb' : 'text-[#858585]'}`}>
                          {t('frameWidth')}
                        </span>
                        <span className={`${isWeb ? 'mt-4 text-12' : 'mt-[6px] text-14'}`}>
                          0-150mm
                        </span>
                      </div>
                      <Image
                        src={`/goods-list/${isWeb ? 'pc' : 'wap'}-size01.png`}
                        alt={t('frameWidthAlt')}
                        width={isWeb ? 42 : 62}
                        height={isWeb ? 42 : 62}
                        className={`h-auto object-contain ${isWeb ? 'w-[42px]' : 'w-[62px]'}`}
                      />
                    </div>
                    <div className="absolute left-[5px]">
                      <Slider
                        value={frameWidthValue}
                        onChange={(_, value) => handleSliderChange('frame_width', value)}
                        onChangeCommitted={(_, value) =>
                          handleSliderChangeCommit('frame_width', value)
                        }
                        valueLabelDisplay="auto"
                        min={0}
                        max={150}
                        step={1}
                        sx={sliderStyle}
                      />
                    </div>
                  </div>

                  {/* Temple 滑块 */}
                  <div className={`${isWeb ? 'mt-[32px]' : 'mt-[20px]'} overflow-hidden mb-[44px]`}>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className={`text-12 ${isWeb ? 'font-bb' : 'text-[#858585]'}`}>
                          {t('temple')}
                        </span>
                        <span className={`${isWeb ? 'mt-4 text-12' : 'mt-[6px] text-14'}`}>
                          0-150mm
                        </span>
                      </div>
                      <Image
                        src={`/goods-list/${isWeb ? 'pc' : 'wap'}-size02.png`}
                        alt={t('templeAlt')}
                        width={isWeb ? 42 : 62}
                        height={isWeb ? 42 : 62}
                        className={`h-auto object-contain ${isWeb ? 'w-[42px]' : 'w-[62px]'}`}
                      />
                    </div>
                    <div className="absolute left-[5px]">
                      <Slider
                        value={templeValue}
                        onChange={(_, value) => handleSliderChange('temple', value)}
                        onChangeCommitted={(_, value) => handleSliderChangeCommit('temple', value)}
                        valueLabelDisplay="auto"
                        min={0}
                        max={150}
                        step={1}
                        sx={sliderStyle}
                      />
                    </div>
                    <div ref={customSizeRef} />
                  </div>
                </div>
              )}
            </div>
          );

        default:
          return null;
      }
    },
    [
      values,
      handleCheckboxChange,
      handleRadioChange,
      handleSliderChange,
      handleSliderChangeCommit,
      getCheckboxOptionClassName,
      getOptionContentClassName,
      getGridClassName,
      getRadioOptionClassName,
      getRadioGridClassName,
      getImageContainerClassName,
      getImageClassName,
      getFilterItemClassName,
      getFilterTitleClassName,
      getFilterTitleTextClassName,
      getArrowIconClassName,
    ],
  );

  return (
    <div
      className="relative max-w-full bg-white transform transition-transform pb-20"
      ref={goodsFilterRef}
    >
      <div className="flex flex-col h-full">
        {!isPopup && getFilterTotal() > 0 && (
          <Button
            onClick={handleClear}
            variant="outlined"
            sx={{
              borderRadius: '6px',
              height: '40px',
              border: '1px solid #DAE1E6',
              color: '#282828',
              fontSize: '14px',
              fontFamily: font.br,
              marginBottom: '24px',
              '&:hover': {
                backgroundColor: 'rgba(128,128,128,0.05)',
              },
            }}
          >
            {globalT('cartPage.clearAll')} {getFilterTotal() > 0 ? ` (${getFilterTotal()})` : ''}
          </Button>
        )}
        {/* 筛选内容 */}
        <div className="flex-1 overflow-y-auto">
          {goodsFilters.map((section) => {
            const currentValue = values[section.id];
            const hasValue = Array.isArray(currentValue)
              ? currentValue.length > 0
              : currentValue && currentValue !== '';
            const isExpanded = expandedSections[section.id];

            return (
              <div key={section.id} className={getFilterItemClassName()}>
                {/* 可点击的标题区域 */}
                <div
                  className={getFilterTitleClassName()}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center">
                    <span className={getFilterTitleTextClassName()}>{section.title}</span>
                    {hasValue && !isWeb && (
                      <span className="ml-[4px] text-14 text-primary-purple">
                        ({Array.isArray(currentValue) ? currentValue.length : 1})
                      </span>
                    )}
                  </div>
                  {isWeb ? (
                    <>
                      {isExpanded ? (
                        <MinusOutlined className="text-[16px] text-[#282828]" />
                      ) : (
                        <PlusOutlined className="text-[16px] text-[#282828]" />
                      )}
                    </>
                  ) : (
                    // 展开/收起箭头
                    <CollapseArrowDownOutlined className={getArrowIconClassName(isExpanded)} />
                  )}
                </div>

                {/* 可折叠的筛选内容 */}
                <Collapse in={isExpanded} timeout="auto">
                  <div className={`${isWeb ? 'pb-[16px]' : 'pb-[14.5px]'}`}>
                    {renderFilterSection(section)}
                  </div>
                </Collapse>
              </div>
            );
          })}
        </div>
        <div className={`${isWeb ? '' : 'h-[100px]'}`} />
        <div className={`${isWeb && isPopup ? 'h-[200px]' : ''}`} />
        {/* 底部按钮 */}
        {isPopup && (
          <div
            className={`flex items-center bg-white fixed bottom-0 ${isWeb ? 'w-[482px] left-0 h-[132px] px-32 justify-between' : 'w-[335PX] right-0 py-[14PX] px-[16PX] gap-[16PX] justify-center'}`}
            style={{ boxShadow: '0 -1px 1.333vw 0 hsla(0,0%,80%,.5)' }}
          >
            <Button
              onClick={handleClear}
              variant="outlined"
              sx={{
                borderRadius: '22px',
                height: '44px',
                width: '105px',
                fontSize: '14px',
                fontFamily: font.bb,
                color: colors.primaryBlack,
                borderColor: '#808080',
              }}
            >
              {t('clear', { total: getFilterTotal() > 0 ? ` (${getFilterTotal()})` : '' })}
            </Button>
            <Button
              onClick={handleApply}
              variant="contained"
              sx={{
                borderRadius: '22px',
                height: '44px',
                width: isWeb ? '290px' : '172px',
                fontSize: '14px',
                fontFamily: font.bb,
                color: '#fff',
              }}
            >
              {t('apply')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
